import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { createKoraClient } from '@architeia/api-client'
import type { CurrentTermsDto, LegalLocale, TermsAcceptanceDto } from '@architeia/types'
import { useAuthStore } from '@/stores/auth'

/**
 * The first-sign-in step: the 18+ confirmation (KORA-154) and the acceptance of the current Terms
 * of Use (KORA-189), asked together on one page. Kora keeps only the time of the age confirmation
 * (KORA-146), never a date of birth, and the accepted Terms version with its time and language.
 *
 * `missing` sends the member to the confirmation page: the age is not confirmed, or Kora requires
 * the Terms (the gate is on and the member has not accepted the current major version). `unknown`
 * (Kora could not be asked) lets them through: sharing stays refused server-side until the age is
 * confirmed, so failing open here costs nothing and an outage does not lock everyone out.
 */
export const useAgeGateStore = defineStore('ageGate', () => {
  const status = ref<'unknown' | 'attested' | 'missing'>('unknown')
  const ageMissing = ref(false)
  /** The member's acceptance record, or null until Kora has answered. */
  const terms = ref<TermsAcceptanceDto | null>(null)
  /** The current version's summary, loaded only when the member has to accept. */
  const currentTerms = ref<CurrentTermsDto | null>(null)
  const confirming = ref(false)
  const error = ref(false)

  const termsRequired = computed(() => terms.value?.required === true)
  /** True when the member accepted an earlier major version: the page shows what changed. */
  const termsUpdated = computed(() => termsRequired.value && terms.value?.acceptedVersion != null)

  function client() {
    return createKoraClient({ getAccessToken: useAuthStore().getAccessToken })
  }

  /** Asks Kora once; a known answer is not asked again until `reset`. */
  async function check(): Promise<'unknown' | 'attested' | 'missing'> {
    if (status.value !== 'unknown') return status.value
    try {
      const attestation = await client().getMyAgeAttestation()
      ageMissing.value = !attestation.attested
    } catch {
      return 'unknown'
    }
    try {
      terms.value = await client().getMyTermsAcceptance()
    } catch {
      terms.value = null
    }
    if (termsRequired.value) {
      try {
        currentTerms.value = await client().getCurrentTerms()
      } catch {
        currentTerms.value = null
      }
    }
    status.value = ageMissing.value || termsRequired.value ? 'missing' : 'attested'
    return status.value
  }

  async function confirm(locale: LegalLocale): Promise<boolean> {
    if (confirming.value) return false
    confirming.value = true
    error.value = false
    try {
      if (ageMissing.value) {
        const result = await client().attestMyAge()
        ageMissing.value = !result.attested
      }
      if (termsRequired.value && terms.value !== null) {
        terms.value = await client().acceptMyTerms(terms.value.currentVersion, locale)
      }
      status.value = ageMissing.value || termsRequired.value ? 'missing' : 'attested'
      return status.value === 'attested'
    } catch {
      // The member stays on the page; a 409 (the terms changed while it was open) is cleared by a reload.
      error.value = true
      return false
    } finally {
      confirming.value = false
    }
  }

  function reset(): void {
    status.value = 'unknown'
    ageMissing.value = false
    terms.value = null
    currentTerms.value = null
    error.value = false
  }

  return {
    status,
    ageMissing,
    terms,
    currentTerms,
    termsRequired,
    termsUpdated,
    confirming,
    error,
    check,
    confirm,
    reset
  }
})
